let headerSpokenOnce = false;
let footerSpokenOnce = false;
let voicesLoaded = false;
let voiceLoadPromise: Promise<void> | null = null;

export function resetHeaderSpokenState() {
  headerSpokenOnce = false;
}

export function isSpeechSupported(): boolean {
  return 'speechSynthesis' in window && typeof window.speechSynthesis !== 'undefined';
}

export function getAvailableVoices(): SpeechSynthesisVoice[] {
  if (!isSpeechSupported()) return [];
  return window.speechSynthesis.getVoices();
}

export function hasEnglishVoice(): boolean {
  const voices = getAvailableVoices();
  return voices.some(voice => voice.lang.startsWith('en'));
}

// Ensure voices are loaded (especially important for Chrome)
export function ensureVoicesLoaded(): Promise<void> {
  if (voicesLoaded) {
    return Promise.resolve();
  }

  if (voiceLoadPromise) {
    return voiceLoadPromise;
  }

  voiceLoadPromise = new Promise((resolve) => {
    const voices = getAvailableVoices();
    if (voices.length > 0) {
      voicesLoaded = true;
      resolve();
      return;
    }

    // Listen for voices to be loaded
    const handleVoicesChanged = () => {
      voicesLoaded = true;
      window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
      resolve();
    };

    window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);

    // Fallback timeout in case voiceschanged never fires
    setTimeout(() => {
      if (!voicesLoaded) {
        voicesLoaded = true;
        window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
        resolve();
      }
    }, 2000);
  });

  return voiceLoadPromise;
}

export function readEntirePage() {
  // Check if speech synthesis is supported
  if (!isSpeechSupported()) {
    console.warn('Speech synthesis is not supported in this browser');
    return;
  }

  // Check if we're in a secure context (required for speech synthesis)
  if (!window.isSecureContext) {
    console.error('Speech synthesis requires HTTPS. Current protocol:', window.location.protocol);
    console.warn('To test speech synthesis, run the app with: ng serve --ssl true --host localhost');
    return;
  }

  // Ensure voices are loaded before proceeding
  ensureVoicesLoaded().then(() => {
    performSpeechSynthesis();
  }).catch(error => {
    console.error('Failed to load voices:', error);
  });
}

function performSpeechSynthesis() {
  const parts: string[] = [];
  const dedupSections = new Set<string>();

  function cleanText(value: string | null | undefined) {
    return value ? value.replace(/\s+/g, ' ').trim() : '';
  }

  function isVisible(el: Element | null): el is HTMLElement {
    if (!el || !(el instanceof HTMLElement)) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    if (el.offsetWidth === 0 && el.offsetHeight === 0) return false;
    if (el.hasAttribute('aria-hidden') && el.getAttribute('aria-hidden') === 'true') return false;
    if (el.hidden) return false;
    return true;
  }

  function shouldSkipElement(el: HTMLElement) {
    const tag = el.tagName.toLowerCase();
    if (['script', 'style', 'noscript'].includes(tag)) return true;
    if (tag === 'mat-icon') return true;
    if (el.classList.contains('mat-icon') || el.classList.contains('icon')) return true;
    if (!isVisible(el)) return true;
    if (tag === 'header' || tag === 'footer') return true; // handled separately
    return false;
  }

  function pushSection(text: string) {
    if (!text || dedupSections.has(text)) return;
    dedupSections.add(text);
    parts.push(text);
  }

  function traverse(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = cleanText(node.textContent);
      if (text) parts.push(text);
      return;
    }

    if (node instanceof HTMLElement) {
      if (shouldSkipElement(node)) return;
      node.childNodes.forEach(traverse);
    }
  }

  function sectionText(selector: string) {
    const el = document.querySelector(selector);
    return isVisible(el) ? cleanText(el?.innerText || '') : '';
  }

  const headerText = sectionText('header');
  if (headerText && !headerSpokenOnce) {
    pushSection(headerText);
    headerSpokenOnce = true;
  }

  traverse(document.body);

  const footerText = sectionText('footer');
  if (footerText && !footerSpokenOnce) {
    pushSection(footerText);
    footerSpokenOnce = true;
  }

  const speechText = parts.join('\n').trim();
  if (speechText && speechText.length > 20) {
    try {
      const utter = new SpeechSynthesisUtterance(speechText);

      // Try to find an English voice, fallback to default
      const voices = getAvailableVoices();
      console.log('Available voices:', voices.map(v => `${v.name} (${v.lang})`));

      const englishVoice = voices.find(voice => voice.lang.startsWith('en'));
      if (englishVoice) {
        utter.voice = englishVoice;
        console.log('Using voice:', englishVoice.name, englishVoice.lang);
      } else {
        console.warn('No English voice found, using default voice');
      }

      utter.lang = 'en-US';
      utter.pitch = 0.6;
      utter.rate = 0.9;
      utter.volume = 1.0;

      // Cancel any ongoing speech only if speech is currently active
      if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
        window.speechSynthesis.cancel();
      }

      // Add error handling
      utter.onerror = (event) => {
        const errorType = event.error;
        console.error('Speech synthesis error:', errorType, event);

        switch (errorType) {
          case 'not-allowed':
            console.warn('Speech synthesis permission denied. This may require user interaction first.');
            console.warn('Try clicking a button to trigger speech synthesis.');
            break;
          case 'interrupted':
            console.warn('Speech synthesis was interrupted. This may be normal if speech was cancelled.');
            break;
          case 'network':
            console.warn('Network error occurred during speech synthesis.');
            break;
          case 'language-unavailable':
            console.warn('Requested language is not available. Available languages:', voices.map(v => v.lang));
            break;
          case 'voice-unavailable':
            console.warn('Requested voice is not available. Available voices:', voices.map(v => v.name));
            break;
          case 'synthesis-unavailable':
            console.warn('Speech synthesis is not available on this device.');
            break;
          case 'synthesis-failed':
            console.warn('Speech synthesis failed unexpectedly.');
            break;
          default:
            console.warn('Unknown speech synthesis error:', errorType);
        }
      };

      utter.onstart = () => {
        console.log('Speech synthesis started successfully');
      };

      utter.onend = () => {
        console.log('Speech synthesis completed');
      };

      utter.onpause = () => {
        console.log('Speech synthesis paused');
      };

      utter.onresume = () => {
        console.log('Speech synthesis resumed');
      };

      console.log('Attempting to speak text length:', speechText.length);
      window.speechSynthesis.speak(utter);
    } catch (error) {
      console.error('Failed to initialize speech synthesis:', error);
    }
  } else {
    console.warn('Speech text too short or empty. Length:', speechText?.length || 0);
  }
}
